import { useMemo, useState, type FC } from 'react';
import { ChevronDown, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import { Field, FieldDescription, FieldLabel } from '@/components/ui/field';
import {
  MultiIdCombobox,
  type MultiIdComboboxOption,
} from '@/components/ui/multi-id-combobox';
import type { ResourceSet } from 'components/IAP/hooks/resourceSetHooks';
import { useGetRostersForGroup } from 'hooks/incident/useRoster';
import { useOrgEventAutomationRules } from 'hooks/orgEvents/orgEventAutomationHooks';
import type { OrgEventChecklistItemDef } from 'hooks/orgEvents/orgEventAutomationHooks';
import { selectGroupGuid, useAppSelector } from 'slices/commonSelectors';
import { DhsResourceDetailsDialog } from '../ResourceTab/DhsResourceDetailsDialog';
import type { ResourceRow } from '../ResourceTab/ResourceTabCard';
import { resourceSetToRow } from '../ResourceTab/resourceSetToRow';
import { cn } from '@/lib/utils';
import { ChecklistConfigEditor } from './ChecklistConfigEditor';

type CreateEventAssetChecklistsProps = {
  resourceOptions: MultiIdComboboxOption[];
  resources: ResourceSet[];
  selectedIds: string[];
  onSelectedIdsChange: (next: string[]) => void;
  checklistsByResourceId: Record<string, OrgEventChecklistItemDef[]>;
  onChecklistsChange: (
    next: Record<string, OrgEventChecklistItemDef[]>
  ) => void;
  userOptions: MultiIdComboboxOption[];
  teamOptions: MultiIdComboboxOption[];
  assignPersonOrRole?: boolean;
  defaultAorIds?: string[];
};

export const CreateEventAssetChecklists: FC<
  CreateEventAssetChecklistsProps
> = ({
  resourceOptions,
  resources,
  selectedIds,
  onSelectedIdsChange,
  checklistsByResourceId,
  onChecklistsChange,
  userOptions,
  teamOptions,
  assignPersonOrRole = false,
  defaultAorIds = [],
}) => {
  const groupGuid = useAppSelector(selectGroupGuid);
  const { data: groupMembers = [] } = useGetRostersForGroup(groupGuid ?? '');
  const { data: automationRules = [] } = useOrgEventAutomationRules();
  const [detailId, setDetailId] = useState<string | null>(null);
  const [dialogMode, setDialogMode] = useState<'view' | 'edit'>('view');
  const [rowPatch, setRowPatch] = useState<Partial<ResourceRow> | null>(null);

  const labelById = new Map(
    resourceOptions.map((option) => [option.value, option.label])
  );

  const detailRow = useMemo(() => {
    if (!detailId) return null;
    const resource = resources.find((row) => row.resource_set_id === detailId);
    if (!resource) return null;
    return {
      ...resourceSetToRow(resource, groupMembers, automationRules),
      ...rowPatch,
    };
  }, [automationRules, detailId, groupMembers, resources, rowPatch]);

  const handleSelectedIdsChange = (next: string[]) => {
    onSelectedIdsChange(next);
    const keep = new Set(next);
    const pruned: Record<string, OrgEventChecklistItemDef[]> = {};
    for (const [id, items] of Object.entries(checklistsByResourceId)) {
      if (keep.has(id)) pruned[id] = items;
    }
    onChecklistsChange(pruned);
    if (detailId && !keep.has(detailId)) setDetailId(null);
  };

  const openDetails = (resourceId: string) => {
    setRowPatch(null);
    setDialogMode('view');
    setDetailId(resourceId);
  };

  return (
    <div className="tw:space-y-3">
      <Field>
        <FieldLabel>Impacted Assets</FieldLabel>
        <MultiIdCombobox
          options={resourceOptions}
          value={selectedIds}
          onChange={handleSelectedIdsChange}
          placeholder="Select resources…"
        />
        <FieldDescription>
          These become the event’s impacted resources. Tasks you attach below
          become org tasks on this event only — they are not saved as resource
          playbooks.
        </FieldDescription>
      </Field>

      {selectedIds.map((resourceId) => (
        <AssetChecklistCard
          key={resourceId}
          title={labelById.get(resourceId) ?? resourceId}
          items={checklistsByResourceId[resourceId] ?? []}
          onChange={(items) =>
            onChecklistsChange({
              ...checklistsByResourceId,
              [resourceId]: items,
            })
          }
          onOpenDetails={() => openDetails(resourceId)}
          onRemove={() =>
            handleSelectedIdsChange(
              selectedIds.filter((id) => id !== resourceId)
            )
          }
          userOptions={userOptions}
          teamOptions={teamOptions}
          assignPersonOrRole={assignPersonOrRole}
          defaultAorIds={defaultAorIds}
        />
      ))}

      <DhsResourceDetailsDialog
        row={detailRow}
        open={!!detailId && !!detailRow}
        onOpenChange={(open) => {
          if (!open) {
            setDetailId(null);
            setDialogMode('view');
            setRowPatch(null);
          }
        }}
        mode={dialogMode}
        onModeChange={setDialogMode}
        openedInEdit={false}
        groupMembers={groupMembers}
        onRowPatch={(patch) =>
          setRowPatch((current) => ({ ...current, ...patch }))
        }
      />
    </div>
  );
};

const AssetChecklistCard: FC<{
  title: string;
  items: OrgEventChecklistItemDef[];
  onChange: (next: OrgEventChecklistItemDef[]) => void;
  onOpenDetails: () => void;
  onRemove: () => void;
  userOptions: MultiIdComboboxOption[];
  teamOptions: MultiIdComboboxOption[];
  assignPersonOrRole?: boolean;
  defaultAorIds?: string[];
}> = ({
  title,
  items,
  onChange,
  onOpenDetails,
  onRemove,
  userOptions,
  teamOptions,
  assignPersonOrRole = false,
  defaultAorIds = [],
}) => {
  const [open, setOpen] = useState(true);
  return (
    <Collapsible
      open={open}
      onOpenChange={setOpen}
      className="tw:rounded-md tw:border tw:border-border"
    >
      <div className="tw:flex tw:w-full tw:items-center tw:gap-2 tw:px-3 tw:py-2">
        <CollapsibleTrigger
          render={
            <button
              type="button"
              aria-label={`Toggle tasks for ${title}`}
              className="tw:inline-flex tw:shrink-0 tw:cursor-pointer tw:border-0 tw:bg-transparent tw:p-0"
            />
          }
        >
          <ChevronDown
            className={cn(
              'tw:h-4 tw:w-4 tw:text-muted-foreground tw:transition-transform',
              open && 'tw:rotate-180'
            )}
          />
        </CollapsibleTrigger>
        <button
          type="button"
          className="tw:min-w-0 tw:flex-1 tw:cursor-pointer tw:truncate tw:border-0 tw:bg-transparent tw:p-0 tw:text-left tw:text-sm tw:font-medium tw:text-foreground"
          onClick={(event) => {
            event.stopPropagation();
            onOpenDetails();
          }}
        >
          {title}
        </button>
        <span className="tw:text-xs tw:text-muted-foreground">
          {items.length === 0
            ? 'No tasks'
            : `${items.length} task${items.length === 1 ? '' : 's'}`}
        </span>
        <Button
          type="button"
          variant="ghost"
          size="icon-sm"
          aria-label={`Remove ${title}`}
          onClick={(event) => {
            event.stopPropagation();
            onRemove();
          }}
        >
          <Trash2 className="tw:size-3.5" />
        </Button>
      </div>
      <CollapsibleContent>
        <div className="tw:space-y-2 tw:border-t tw:border-border tw:p-3">
          <ChecklistConfigEditor
            items={items}
            onChange={onChange}
            userOptions={userOptions}
            teamOptions={teamOptions}
            allowTemplateVariables={false}
            addLabel="Add Task"
            emptyMessage="No per-asset response plans yet."
            assignPersonOrRole={assignPersonOrRole}
            defaultAorIds={defaultAorIds}
          />
        </div>
      </CollapsibleContent>
    </Collapsible>
  );
};
